"use client"

import { Form, Row, Col, Button, Input, DatePicker, Select, message, Tooltip, Divider, Descriptions, Result } from "antd"
import { useEffect, useState } from "react"
import "../../scss/style.scss"
import helper from "../../utils/helper"
import { errorMessage } from "../../utils/commonStringsConsts"
import { stepperMoving } from "../../stores/actions"
import { getApi, request } from '../../../../shared/src/apis';
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import { createFreePeriodApi, createFreePeriodForPartnersApi, getPartnersApi } from "../../utils/apisConsts"
import { InfoCircleOutlined } from '@ant-design/icons';
import { ModalComponent } from '@operations/shared';

enum errorsType {
    company = 'Company',
    merchant = 'Merchant',
}

export default function DiscountOffer() {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [serviceTypes, setServiceTypes] = useState<any[]>([]);
    const [loadServices, setLoadServices] = useState(false);
    const [partners, setPartners] = useState<any[]>([]);
    const [loadPartners, setLoadPartners] = useState(false);
    const [sendingData, setSendingData] = useState(false);
    const [selectedPartners, setSelectedPartners] = useState<any[]>([]);
    const [errorReasons, setErrorReasons] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const { Option } = Select;
    const offerData = useSelector((state: any) => state.actionsReducer?.offerData)
    const minNumberOfInstallments = Form.useWatch("minNumberOfInstallments", form);
    const companyMaxInstallments = Number(offerData?.selectedCompanyInfo?.maxInstallmentCount ?? offerData?.maxInstallmentCount);
    const hasCompanyMaxInstallments = Number.isFinite(companyMaxInstallments) && companyMaxInstallments > 0;
    const minInstallmentsWarning =
        offerData?.isCompany && hasCompanyMaxInstallments && Number(minNumberOfInstallments) === companyMaxInstallments
            ? `Warning: Minimum months equals max installments (${companyMaxInstallments}).`
            : "";
    useEffect(() => {
        form.resetFields()
    }, [form])

    useEffect(() => {
        if (offerData?.isCompany) storeServiceGroupsTypes()
        else storePartners()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerData?.isCompany])

    const storeServiceGroupsTypes = async () => {
        try {
            setLoadServices(true);
            const getServiceTypes: any = await helper.getServiceTypes();
            if (getServiceTypes?.status !== 200) {
                setLoadServices(false);
                message.error(`${errorMessage} with service groups types`);
                return;
            } else {
                setServiceTypes(getServiceTypes?.data?.data?.servicesGroups);
            }
        } catch (error) {
            message.error(`${errorMessage} with service groups types`);
        }
        setLoadServices(false);
    };
    const storePartners = async () => {
        try {
            setLoadPartners(true);
            const getPartners: any = await dispatch(getApi(getPartnersApi + offerData?.offerTypeId, {}));
            if (getPartners?.payload?.status !== 200) {
                setLoadPartners(false);
                message.error(`${errorMessage} with partners`);
                return;
            } else {
                setPartners(getPartners?.payload?.data);
                const allPartnerIds = getPartners?.payload?.data?.map((partner: any) => partner?.id) || [];
                setSelectedPartners(allPartnerIds);
                form.setFieldsValue({ partnerID: allPartnerIds });
            }
        } catch (error) { message.error(`${errorMessage} with partners`); }
        setLoadPartners(false);
    }
    const dropDownSearch = (input: string, option: { children?: string; label?: string; }) => {
        const searchText = option?.label || option?.children || '';
        return searchText.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    }
    const createEarlyPayment = async (values: any) => {
        try {
            setSendingData(true)
            const response: { [key: string]: any } = await dispatch(request({
                url: offerData?.isCompany ? createFreePeriodApi : createFreePeriodForPartnersApi, method: "POST",
                data: {
                    companyID: offerData?.isCompany ? offerData?.offerTypeId : undefined,
                    serviceGroupID: offerData?.isCompany ? values?.serviceGroupID : offerData?.offerTypeId,
                    partnerIDs: offerData?.isCompany ? undefined : selectedPartners,
                    startDate: moment(values?.startDate).format("YYYY-MM-DD"),
                    endDate: moment(values?.endDate).format("YYYY-MM-DD"),
                    numberOfFreeMonths: Number(values?.numberOfFreeMonths),
                    maxAmountPerUser: Number(values?.maxAmountPerUser),
                    firstTransactionUser: values?.firstTransactionUser,
                    maxNumberOfRequestsPerUser: Number(values?.maxNumberOfRequestsPerUser),
                    minNumberOfInstallments: Number(values?.minNumberOfInstallments)
                }
            }))
            if (response?.data?.status === 200 || response?.data?.status === 201) {
                message.success(response?.data?.message)
                dispatch(stepperMoving(0))
                form.resetFields()
            } else {
                if (response?.data?.status === 409) {
                    setErrorReasons(response?.data?.errors);
                    setShowModal(true);
                } else {
                    message.error(response?.data?.message)
                }
            }
            setSendingData(false)
        }
        catch (error: any) {
            setSendingData(false)
            if (error?.response?.status === 409) {
                setErrorReasons(error?.response?.data?.errors);
                setShowModal(true);
            } else {
                message.error(error?.response?.status === 400 ? error?.response?.data?.message : errorMessage)
            }
        }
    }

    const handleChange = (value: any) => {
        setSelectedPartners(value);
        form.setFieldsValue({ partnerID: value });
    }

    const handleSelectAll = () => {
        const allPartnerIds = partners?.map((partner: any) => partner?.id);
        setSelectedPartners(allPartnerIds);
        form.setFieldsValue({ partnerID: allPartnerIds });
    }

    const handleClearAll = () => {
        setSelectedPartners([]);
        form.setFieldsValue({ partnerID: [] });
    }

    const generateErrorsTable = () => {
        const rows = errorReasons && Array.isArray(errorReasons) &&
            errorReasons.map((errorRow: { type: errorsType, message: string, name: string }) => <Descriptions.Item span={4} label={errorRow.name}>{errorRow.message}</Descriptions.Item>)
        return <>
            <Result
                status="error"
                style={{ padding: "30px" }}
                title={<p style={{ fontSize: '18px', padding: 0 }}>Offer can’t be applied. Please suspend other offers and try again.</p>}
            />
            <Descriptions bordered>
                <Descriptions.Item span={3} labelStyle={{ minWidth: "100px" }} label={<b>{offerData?.isCompany ? "Companies" : "Merchants"}</b>}><b>Offer Details</b></Descriptions.Item>
                {rows}
            </Descriptions>
        </>
    }

    return (
        <>
            <div className="CategoriesAndBrands">
                <Form
                    form={form}
                    onFinish={createEarlyPayment}
                    autoComplete="off"
                    layout="vertical"
                    initialValues={{ maxAmountPerUser: 100000, maxNumberOfRequestsPerUser: 1, firstTransactionUser: false }}
                >
                    <Row gutter={[24, 10]}>
                        {offerData?.isCompany ?

                            <Col span={24}>
                                <label >
                                    Service groups <span>*</span>
                                </label>
                                <Form.Item name={"serviceGroupID"} rules={[{ required: true, message: "Required" }]}>
                                    <Select style={{ width: '100%' }}
                                        placeholder={'Please select the services'}
                                        showSearch
                                        allowClear
                                        loading={loadServices}
                                        disabled={loadServices}
                                        filterOption={((input, option: any) => dropDownSearch(input, option))}
                                    >
                                        {Array.isArray(serviceTypes) && serviceTypes?.map((service: any, key: number) => {
                                            return <Option value={service?.id} data={service} key={key}>{service?.name}</Option>
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>
                            :
                            <Col span={24}>
                                <label >
                                    Partner <span>*</span>
                                </label>
                                <Form.Item
                                    name={"partnerID"}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value || value.length === 0) {
                                                    return Promise.reject("At least one partner must be selected");
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Select
                                        mode="multiple"
                                        style={{ width: '100%' }}
                                        placeholder="Please select partners"
                                        value={selectedPartners}
                                        onChange={handleChange}
                                        allowClear
                                        showSearch
                                        showArrow
                                        filterOption={((input, option: any) => dropDownSearch(input, option))}
                                        loading={loadPartners}
                                        disabled={loadPartners}
                                        options={partners?.map((partner: any) => ({ label: partner?.name, value: partner?.id }))}
                                        dropdownRender={(menu) => (
                                            <div>
                                                <div style={{ padding: "4px 8px", display: "flex", gap: "8px" }}>
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={handleSelectAll}
                                                        style={{ padding: 0, border: "none" }}
                                                    >
                                                        Select All
                                                    </Button>
                                                    <Divider type="vertical" style={{ margin: 0 }} />
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={handleClearAll}
                                                        style={{ padding: 0, border: "none" }}
                                                    >
                                                        Clear All
                                                    </Button>
                                                </div>
                                                <Divider style={{ margin: "4px 0" }} />
                                                {menu}
                                            </div>
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        }
                        <Col xs={24} sm={12}>
                            <label >
                                Start Date<span>*</span>
                            </label>
                            <Form.Item
                                name={"startDate"}
                                rules={[
                                    { required: true, message: "Required" },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            if (value.isBefore(moment().startOf('day'))) {
                                                return Promise.reject("Start date cannot be in the past");
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                dependencies={["endDate"]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="Add a start date"
                                    disabledDate={current => current && current < moment().startOf('day')}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <label >
                                End Date<span>*</span>
                            </label>
                            <Form.Item
                                name={"endDate"}
                                dependencies={["startDate"]}
                                rules={[
                                    { required: true, message: "Required" },
                                    {
                                        validator: (rule, value, callback) => {
                                            const startDate = form.getFieldValue("startDate");
                                            if (!value) return Promise.resolve();
                                            if (value?.isBefore(moment().startOf('day'))) {
                                                return Promise.reject("End date cannot be in the past");
                                            }
                                            if (startDate && value?.isSameOrBefore(startDate, 'day')) {
                                                return Promise.reject("End date must be after start date");
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    placeholder="Add an end date"
                                    disabledDate={current => current && current < moment().startOf('day')}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <label >
                                Number of Free Months <span>*</span>
                            </label>
                            <Form.Item name={"numberOfFreeMonths"}
                                rules={[
                                    { required: true, message: "Required" },
                                    { type: "number", min: 1, message: "Must be at least 1" }]}
                                normalize={value => (value === "" ? undefined : Number(value))}
                            >
                                <Input
                                    type="number"
                                    min={1}
                                    placeholder={'Add number of free months '} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <label>
                                Minimum Number Of Installments <span>*</span>
                            </label>
                            <Form.Item
                                name={"minNumberOfInstallments"}
                                rules={[
                                    { required: true, message: "Required" },
                                    { type: "number", min: 1, message: "Must be at least 1" },
                                    {
                                        validator: (_, value) => {
                                            const numberOfFreeMonths = form.getFieldValue("numberOfFreeMonths");
                                            if (!value) return Promise.resolve();
                                            if (numberOfFreeMonths && value < numberOfFreeMonths && offerData?.isCompany) {
                                                return Promise.reject("Must be equal or greater than the number of free months");
                                            }
                                            if (offerData?.isCompany && hasCompanyMaxInstallments && value > companyMaxInstallments) {
                                                return Promise.reject(`Minimum months must be less than max installments (${companyMaxInstallments}).`);
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                dependencies={["numberOfFreeMonths"]}
                                normalize={value => (value === "" ? undefined : Number(value))}
                                validateStatus={minInstallmentsWarning ? "warning" : undefined}
                                help={minInstallmentsWarning || undefined}
                            >
                                <Input
                                    type="number"
                                    min={1}
                                    placeholder={'Add number of installments'} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <label> Max Allowed Amount </label>
                                <Tooltip title="Leave this field empty if you don’t want to have a limit on the total requests` amounts">
                                    <InfoCircleOutlined style={{ marginBottom: '7px', color: 'red' }} />
                                </Tooltip>
                                &nbsp;
                            </div>
                            <Form.Item
                                name={"maxAmountPerUser"}
                                rules={[
                                    { type: "number", min: 0, message: "Must be a positive number" }
                                ]}
                                normalize={value => (value === "" ? undefined : Number(value))}
                            >
                                <Input type="number" min={0} placeholder={`No Max Amount selected`} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <label>Max Number Of Requests Per User</label>
                                <Tooltip title="Leave this field empty if you don’t want to have a limit for the number of requests.">
                                    <InfoCircleOutlined style={{ marginBottom: '7px', color: 'red' }} />
                                </Tooltip>
                                &nbsp;
                            </div>
                            <Form.Item
                                name={"maxNumberOfRequestsPerUser"}
                                rules={[
                                    { type: "number", min: 1, message: "Must be at least 1" }
                                ]}
                                normalize={value => (value === "" ? undefined : Number(value))}
                            >
                                <Input
                                    type="number"
                                    min={1}
                                    placeholder={`No Max Number of Requests selected`}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <label >
                                Activate Upon First Request
                            </label>
                            <Form.Item
                                name={"firstTransactionUser"}
                                valuePropName="checked">
                                <Input type="checkbox" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} style={{ textAlign: "right", marginTop: "20px", display: "flex", justifyContent: "flex-end" }} >
                            <Button type="primary" htmlType="submit" loading={sendingData}>
                                Submit
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>
            <ModalComponent
                content={generateErrorsTable()}
                visibleModal={showModal}
                handleCloseModal={() => setShowModal(false)}
                handleAcceptModal={() => setShowModal(false)}
                cancelText="Close"
                okText="Ok"
            />
        </>
    )
}

